import { useEffect } from "react";
import { toast } from "react-toastify";
import useRefreshToken from "./useRefreshToken";
import useAuth from "./useAuth";
import { axiosPrivate } from "../api/axios";
// import { clearSession } from "../services/LocalStorage";
import { clearSession } from "../services/SessionStorage";
import { useSelector } from "react-redux";
import { selectActiveWinery } from "../context/selectors";
import { getItem } from "../services/LocalStorage";
import { useNavigate } from "react-router-dom";
import { buildLoginRedirectFrom } from "../services/redirect";

// Cattura il path corrente (da window.location, non da un closure di useLocation
// che potrebbe essere stale nell'interceptor) per tornarci dopo il re-login.
const redirectToLogin = (navigate) => {
  const from = buildLoginRedirectFrom(
    `${window.location.pathname}${window.location.search}`
  );
  navigate("/login", { state: { from } });
};

const useAxiosPrivate = () => {
  const refresh = useRefreshToken();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const activeWinery = useSelector(selectActiveWinery);

  useEffect(() => {
    const requestIntercept = axiosPrivate.interceptors.request.use(
      (config) => {
        if (!config.headers["Authorization"]) {
          // is the first attempt
          config.headers["Authorization"] = `Bearer ${auth.accessToken}`; // taken from initial login
        }
          config.headers["Accept-Language"] = getItem('i18LanguageLocale')?.split("_")?.[0] || "it";
        if (activeWinery) {
          config.headers["clientname"] = activeWinery?.client_name || "";
          config.headers["wineryid"] = String(activeWinery?.id) || "";
        }

        return config;
      },
      (error) => Promise.reject(error)
    );
 
    const responseIntercept = axiosPrivate.interceptors.response.use(
      response => response,
      async (error) => {
        const prevRequest = error?.config;

        // 🔁 TOKEN SCADUTO → refresh
        if (
          error?.response?.status === 401 &&
          error?.response?.data?.error === "TOKEN_EXPIRED" &&
          !prevRequest?.sent
        ) {
          prevRequest.sent = true;

          try {
            const newAccessToken = await refresh();
            prevRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return axiosPrivate(prevRequest);
          } catch (refreshError) {
            redirectToLogin(navigate);
            return Promise.reject(refreshError);
          }
        }

        // 🚫 TOKEN INVALIDO → logout
        if (
          error?.response?.status === 403 &&
          error?.response?.data?.error === "INVALID_TOKEN"
        ) {
          redirectToLogin(navigate);
          return Promise.reject(error);
        }

        // ⛔ NON AUTORIZZATO (RBAC)
        if (error?.response?.status === 403) {
          // Le mutazioni passano da toastAxios, che mostra già il proprio toast
          // sul 403. Qui notifichiamo solo le letture (GET) negate, che altrimenti
          // verrebbero inghiottite silenziosamente dai service.
          if ((prevRequest?.method || "get").toLowerCase() === "get") {
            // Messaggio statico: useLabels non è utilizzabile qui (dipende a sua
            // volta da useAxiosPrivate → ricorsione infinita di hook).
            const data = error?.response?.data;
            const reqId = data?.reqId || error?.response?.headers?.["x-request-id"];
            const resource = data?.details?.resource;
            const action = data?.details?.action;
            const lang = getItem("i18LanguageLocale")?.split("_")?.[0] || "it";
            const base = lang === "en" ? "Access denied" : "Accesso negato";
            const detail = resource ? `: ${resource} (${action})` : "";
            const ref = reqId ? ` [${reqId}]` : "";
            toast.error(`${base}${detail}${ref}`, {
              toastId: `forbidden-read-${resource || "unknown"}`,
              position: toast.POSITION.BOTTOM_RIGHT,
              autoClose: reqId ? 8000 : 4000,
            });
          }
          return Promise.reject({ ...error, isForbidden: true });
        }

        // 🚪 NON LOGGATO
        if (error?.response?.status === 401) {
          redirectToLogin(navigate);
          return Promise.reject(error);
        }

        // 🏭 winery non selezionata
        if (error?.response?.status === 428) {
          const activeWineryId = getItem("activeWineryId");
          if (!activeWineryId) {
            navigate("/#wineriesList");
          }
        }

        // Errori 4xx di business (es. validazione, operazione non completabile):
        // non invalidare la sessione, propaga l'errore al chiamante
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return Promise.reject(error);
        }

        // Solo per errori gravi/inattesi (5xx, network errors): clear session
        clearSession("authToken");
        return Promise.reject(error);
      }
    );


    // const responseIntercept = axiosPrivate.interceptors.response.use(
    //   (response) => response, // if the response is good, we return it
    //   async (error) => {
    //     const prevRequest = error?.config;
        
    //     // if (error?.response?.status === 403 && !prevRequest?.sent) {
    //     //   // token expired
    //     //   prevRequest.sent = true; // get rid of loops
    //     //   const newAccessToken = await refresh();
    //     //   prevRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
    //     //   return axiosPrivate(prevRequest);
    //     // } else if (error?.response?.status === 401 && !prevRequest?.sent) {
    //     //   // jwt expired
    //     //   prevRequest.sent = true; // get rid of loops
    //     //   navigate('/login');
    //     //   // const newAccessToken = await refresh();
    //     //   // prevRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
    //     //   // return axiosPrivate(prevRequest);
    //     // }

    //     // 403 → potrebbe essere token scaduto o permesso negato
    //     if (error?.response?.status === 403 && !prevRequest?.sent) {
    //       if (error?.response?.data?.tokenExpired) {
    //         // token scaduto → refresh
    //         prevRequest.sent = true;
    //         const newAccessToken = await refresh();
    //         prevRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
    //         return axiosPrivate(prevRequest);
    //       } else {
    //         // loggato ma senza permesso → non sloggare
    //         return Promise.reject({ ...error, isForbidden: true });
    //       }
    //     } 
    //     // 401 → non loggato o token invalido
    //     else if (error?.response?.status === 401 && !prevRequest?.sent) {
    //       prevRequest.sent = true;
    //       navigate('/login');
    //       return Promise.reject(error);
    //     } else if (error?.response?.status === 428) {
    //       const activeWineryId = getItem("activeWineryId");

    //       if (!activeWineryId) {
    //         // Opzione 1: naviga alla lista cantine
    //         navigate("/#wineriesList");

    //         // Opzione 2 (meglio se usi modale):
    //         // dispatch(openWinerySelectionModal({ wineries: error.response.data.wineries }));
    //       }
    //     }

    //     // fallback generico: clear session e reject
    //     clearSession("authToken");
    //     return Promise.reject(error);
    //   }
    // );

    // clean up function
    return () => {
      axiosPrivate.interceptors.request.eject(requestIntercept);
      axiosPrivate.interceptors.response.eject(responseIntercept);
    };
  }, [auth, refresh]);

  return axiosPrivate;
};

export default useAxiosPrivate;
